(() => {
  const CONTAINER_ID = 'CartDrawer-Recommendations';
  const SECTION_ID = 'cart-recommendations';
  const DRAWER_OPEN_CLASSNAMES = ['active', 'open', 'is-visible'];
  let queueToken = 0;
  let loadController = null;
  let lastProductId = null;
  let latestCartState = null;
  let prefetchedKey = null;
  let prefetchedMarkup = null;
  let prefetchedPromise = null;
  let unsubscribeCartUpdate;

  function getDrawer() {
    return document.querySelector('cart-drawer');
  }

  function getProductIdFromElement(element) {
    const productContainer = element?.closest?.('[data-product-id]');
    const productId = productContainer?.dataset?.productId;
    return productId ? Number.parseInt(productId, 10) : null;
  }

  function getContainer() {
    return document.getElementById(CONTAINER_ID);
  }

  function clearContainer(container) {
    if (!container) return;
    container.innerHTML = '';
    lastProductId = null;
  }

  function afterDrawerRender(callback) {
    requestAnimationFrame(() => {
      requestAnimationFrame(callback);
    });
  }

  function queueLoad() {
    const currentToken = ++queueToken;

    afterDrawerRender(() => {
      if (currentToken !== queueToken) return;
      loadCartRecommendations();
    });
  }

  function isAbortError(error) {
    return error && error.name === 'AbortError';
  }

  async function fetchCart(signal) {
    const response = await fetch(`${window.Shopify.routes.root}cart.js`, {
      credentials: 'same-origin',
      cache: 'no-store',
      signal,
    });

    if (!response.ok) return null;
    return response.json();
  }

  function getProductId(cart) {
    if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) return null;
    return cart.items[cart.items.length - 1]?.product_id || null;
  }

  function syncCartState(cartData) {
    if (!cartData) return;

    latestCartState = cartData;

    if (cartData.item_count === 0) {
      lastProductId = null;
      return;
    }

    if (Array.isArray(cartData.items) && cartData.items.length > 0) {
      lastProductId = getProductId(cartData);
      primeRecommendations(lastProductId);
      return;
    }

    if (cartData.product_id) {
      lastProductId = cartData.product_id;
      primeRecommendations(lastProductId);
    }
  }

  function getLimit(container) {
    const renderedLimit = Number.parseInt(container.querySelector('.cart-recommendations')?.dataset.limit || '', 10);
    if (Number.isFinite(renderedLimit) && renderedLimit > 0) return renderedLimit;

    return Number.parseInt(container.dataset.limit || '4', 10) || 4;
  }

  function getCurrentLimit() {
    const container = getContainer();
    if (!container) return 4;
    return getLimit(container);
  }

  function getRecommendationsKey(productId, limit) {
    return `${window.Shopify.routes.root}:${window.Shopify.currency?.active || ''}:${productId}:${limit}`;
  }

  function getRecommendationsUrl(productId, limit) {
    return (
      `${window.Shopify.routes.root}recommendations/products?product_id=${encodeURIComponent(productId)}` +
      `&limit=${encodeURIComponent(limit)}` +
      `&section_id=${encodeURIComponent(SECTION_ID)}`
    );
  }

  async function fetchRecommendationsMarkup(productId, limit, signal) {
    const response = await fetch(getRecommendationsUrl(productId, limit), {
      credentials: 'same-origin',
      cache: 'no-store',
      signal,
    });

    if (!response.ok) return null;
    return response.text();
  }

  function primeRecommendations(productId) {
    if (!productId) return;

    const limit = getCurrentLimit();
    const key = getRecommendationsKey(productId, limit);
    if (prefetchedKey === key && (prefetchedMarkup || prefetchedPromise)) return;

    prefetchedKey = key;
    prefetchedMarkup = null;
    prefetchedPromise = fetchRecommendationsMarkup(productId, limit)
      .then((markup) => {
        if (prefetchedKey === key) {
          prefetchedMarkup = markup;
        }
        return markup;
      })
      .catch(() => null);
  }

  function primeRecommendationsFromForm(form) {
    const productId = getProductIdFromElement(form);
    if (productId) {
      primeRecommendations(productId);
    }
  }

  function updateCarouselControls(section) {
    const viewport = section.querySelector('[data-recommendations-viewport]');
    const previousButton = section.querySelector('[data-recommendations-prev]');
    const nextButton = section.querySelector('[data-recommendations-next]');
    if (!viewport || !previousButton || !nextButton) return;

    const maxScrollLeft = Math.max(viewport.scrollWidth - viewport.clientWidth, 0);
    previousButton.disabled = viewport.scrollLeft <= 4;
    nextButton.disabled = viewport.scrollLeft >= maxScrollLeft - 4;
  }

  function getScrollDistance(viewport) {
    const tile = viewport.querySelector('.cart-recommendations__item');
    if (!tile) return Math.round(viewport.clientWidth * 0.8);

    const track = viewport.querySelector('.cart-recommendations__track');
    const trackStyles = track ? window.getComputedStyle(track) : null;
    const gap = trackStyles
      ? Number.parseFloat(trackStyles.columnGap || trackStyles.gap || '0')
      : 0;

    return Math.round(tile.getBoundingClientRect().width + gap);
  }

  async function submitRecommendationForm(form) {
    const drawer = getDrawer();
    const submitButton = form.querySelector('[type="submit"]');
    const spinner = form.querySelector('.loading__spinner');
    if (!submitButton) return;

    submitButton.setAttribute('aria-disabled', 'true');
    submitButton.classList.add('loading');
    spinner?.classList.remove('hidden');

    try {
      const formData = new FormData(form);

      if (drawer && typeof drawer.getSectionsToRender === 'function') {
        formData.append(
          'sections',
          drawer.getSectionsToRender().map((section) => section.id)
        );
        formData.append('sections_url', window.location.pathname);
        if (typeof drawer.setActiveElement === 'function') {
          drawer.setActiveElement(document.activeElement);
        }
      }

      const response = await fetch(routes.cart_add_url, {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: formData,
        credentials: 'same-origin',
      });

      const cartResponse = await response.json();
      if (cartResponse.status) return;

      syncCartState(cartResponse);

      if (drawer && cartResponse.sections) {
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          await publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-recommendations',
            productVariantId: formData.get('id'),
            cartData: cartResponse,
          });
        }

        drawer.renderContents(cartResponse);
      } else {
        window.location = routes.cart_url;
      }
    } catch (error) {
      if (!isAbortError(error)) console.error(error);
    } finally {
      submitButton.classList.remove('loading');
      submitButton.removeAttribute('aria-disabled');
      spinner?.classList.add('hidden');
    }
  }

  function initRecommendationForms(container) {
    container.querySelectorAll('[data-cart-recommendation-form]').forEach((form) => {
      if (form.dataset.initialized === 'true') return;

      form.addEventListener('submit', (event) => {
        event.preventDefault();
        submitRecommendationForm(form);
      });

      form.dataset.initialized = 'true';
    });
  }

  function initCarousel(container) {
    const section = container.querySelector('.cart-recommendations');
    if (!section) return;

    const viewport = section.querySelector('[data-recommendations-viewport]');
    const previousButton = section.querySelector('[data-recommendations-prev]');
    const nextButton = section.querySelector('[data-recommendations-next]');

    initRecommendationForms(container);

    if (!viewport || section.dataset.carouselInitialized === 'true') {
      updateCarouselControls(section);
      return;
    }

    viewport.addEventListener(
      'scroll',
      () => {
        updateCarouselControls(section);
      },
      { passive: true }
    );

    previousButton?.addEventListener('click', () => {
      viewport.scrollBy({ left: -getScrollDistance(viewport), behavior: 'smooth' });
    });

    nextButton?.addEventListener('click', () => {
      viewport.scrollBy({ left: getScrollDistance(viewport), behavior: 'smooth' });
    });

    section.dataset.carouselInitialized = 'true';
    updateCarouselControls(section);
  }

  async function loadCartRecommendations() {
    const container = getContainer();
    if (!container) return;

    loadController?.abort();
    const controller = new AbortController();
    loadController = controller;

    try {
      let cart = latestCartState;

      if (!cart || (!Array.isArray(cart.items) && !cart.product_id && cart.item_count !== 0)) {
        cart = await fetchCart(controller.signal);
        syncCartState(cart);
      }

      if (!cart || cart.item_count === 0) {
        clearContainer(container);
        return;
      }

      const productId = getProductId(cart) || cart.product_id || lastProductId;
      if (!productId) {
        clearContainer(container);
        return;
      }

      if (productId === lastProductId && container.querySelector('.cart-recommendations')) {
        initCarousel(container);
        return;
      }

      const limit = getLimit(container);
      const key = getRecommendationsKey(productId, limit);
      let markup = null;

      if (prefetchedKey === key) {
        markup = prefetchedMarkup || (prefetchedPromise ? await prefetchedPromise : null);
      }

      if (!markup) {
        markup = await fetchRecommendationsMarkup(productId, limit, controller.signal);
      }

      if (!markup) return;
      const documentFragment = new DOMParser().parseFromString(markup, 'text/html');
      const section = documentFragment.getElementById(`shopify-section-${SECTION_ID}`);
      const recommendations = section?.querySelector('.cart-recommendations');

      if (!recommendations || !recommendations.querySelector('[data-recommendations-viewport]')) {
        clearContainer(container);
        return;
      }

      container.innerHTML = recommendations.outerHTML;
      container.dataset.limit = recommendations.dataset.limit || '';
      lastProductId = productId;
      initCarousel(container);
    } catch (error) {
      if (!isAbortError(error)) console.error(error);
    } finally {
      if (loadController === controller) {
        loadController = null;
      }
    }
  }

  function observeDrawerOpenState() {
    const drawer = getDrawer();
    const drawerPanel = document.getElementById('CartDrawer');
    if (!drawer) return;

    let wasOpen = false;

    const checkDrawerState = () => {
      const isOpen =
        DRAWER_OPEN_CLASSNAMES.some((className) => drawer.classList.contains(className)) ||
        (drawerPanel && DRAWER_OPEN_CLASSNAMES.some((className) => drawerPanel.classList.contains(className)));

      if (isOpen && !wasOpen) queueLoad();
      wasOpen = isOpen;
    };

    const observer = new MutationObserver(checkDrawerState);
    observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });

    if (drawerPanel) {
      observer.observe(drawerPanel, { attributes: true, attributeFilter: ['class'] });
    }
  }

  function hookDrawerOpen() {
    customElements.whenDefined('cart-drawer').then(() => {
      const DrawerClass = customElements.get('cart-drawer');
      if (!DrawerClass || DrawerClass.prototype.cartRecommendationsOpenHooked) return;

      const originalOpen = DrawerClass.prototype.open;
      const originalRenderContents = DrawerClass.prototype.renderContents;

      DrawerClass.prototype.open = function (...args) {
        const returnValue = originalOpen.apply(this, args);
        document.dispatchEvent(new CustomEvent('cart-drawer:open'));
        queueLoad();
        return returnValue;
      };

      DrawerClass.prototype.renderContents = function (...args) {
        syncCartState(args[0]);
        const returnValue = originalRenderContents.apply(this, args);
        loadCartRecommendations();
        return returnValue;
      };

      DrawerClass.prototype.cartRecommendationsOpenHooked = true;
    });
  }

  function hookDrawerItemsUpdate() {
    customElements.whenDefined('cart-drawer-items').then(() => {
      const DrawerItemsClass = customElements.get('cart-drawer-items');
      if (!DrawerItemsClass || DrawerItemsClass.prototype.cartRecommendationsUpdateHooked) return;

      const originalOnCartUpdate = DrawerItemsClass.prototype.onCartUpdate;
      if (typeof originalOnCartUpdate !== 'function') return;

      DrawerItemsClass.prototype.onCartUpdate = function (...args) {
        const result = originalOnCartUpdate.apply(this, args);

        if (result && typeof result.finally === 'function') {
          return result.finally(() => {
            loadCartRecommendations();
          });
        }

        loadCartRecommendations();
        return result;
      };

      DrawerItemsClass.prototype.cartRecommendationsUpdateHooked = true;
    });
  }

  function subscribeToThemeCartEvents() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    unsubscribeCartUpdate = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      syncCartState(event?.cartData);
      queueLoad();
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    hookDrawerOpen();
    hookDrawerItemsUpdate();
    observeDrawerOpenState();
    subscribeToThemeCartEvents();
    document.addEventListener(
      'submit',
      (event) => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement)) return;

        const action = (form.getAttribute('action') || form.action || '').toLowerCase();
        if (!action.includes('/cart/add')) return;

        primeRecommendationsFromForm(form);
      },
      true
    );
    document.addEventListener('cart-drawer:open', queueLoad);
    window.addEventListener('resize', () => {
      const section = getContainer()?.querySelector('.cart-recommendations');
      if (section) updateCarouselControls(section);
    });
    primeRecommendationsFromForm(document.querySelector('form[action*="/cart/add"]'));
    queueLoad();
  });

  window.addEventListener('beforeunload', () => {
    unsubscribeCartUpdate?.();
    loadController?.abort();
  });

  window.loadCartRecommendations = loadCartRecommendations;
})();
